import { useUser } from '@clerk/clerk-react';
import type { WalkthroughGroup } from '@mesa/core/pull';
import { SidebarSimpleIcon } from '@phosphor-icons/react';
import { useParams } from '@tanstack/react-router';
import { BotMessageSquareIcon, MessagesSquareIcon, SparklesIcon, RefreshCwIcon } from 'lucide-react';
import { motion } from 'motion/react';
import { AnimatePresence } from 'motion/react';
import { useEffect, useMemo, useRef, useState, useContext } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { toast } from 'sonner';

import { AiChatProvider } from '../context/ai-chat-context';
import { SidebarProvider, useSidebar } from '../context/sidebar-context';
import { usePullActions } from '../hooks/use-pull-actions';
import { usePullDataWithRefresh } from '../hooks/use-pull-data';

import { MergeDialog } from './merge-dialog';
import { PullActionButton } from './pull-action-button';
import { PullActivityPopover } from './pull-activity-popover';
import { PullChecksPopover } from './pull-checks-popover';
import { PullDescription } from './pull-description';
import { PullDiscussion } from './pull-discussion';
import { StateCell } from './pull-list';
import { PullWalkthrough } from './pull-walkthrough';

import { FilePathsTree } from '@/components/file-paths-tree';
import { ReviewersAndReviewTrigger } from '@/components/reviewers-and-review-trigger';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { CommentsSidebar } from '@/features/comments/components/comments-sidebar';
import { MergeStrategy } from '@/features/pulls/services/pulls';
import { cn } from '@/lib/utils';
import { useCurrentOrg } from '@/services/org';

export function PullDetail() {
  return (
    <ErrorBoundary fallback={<div className="p-4">Error loading pull request</div>}>
      <SidebarProvider>
        <PullDetailInner />
      </SidebarProvider>
    </ErrorBoundary>
  );
}

function PullDetailInner() {
  const { owner, repo, number } = useParams({ from: '/_authed/_layout/$owner/$repo/pull/$number' });
  const [isMergeDialogOpen, setIsMergeDialogOpen] = useState(false);
  const [defaultMergeStrategy, setDefaultMergeStrategy] = useState<MergeStrategy>('merge');
  const [isHeaderTitleVisible, setIsHeaderTitleVisible] = useState(false);

  // Use the sidebar context instead of local state
  const { isRightSidebarOpen, activeTab, dispatch } = useSidebar();
  const [isLeftSidebarOpen, setIsLeftSidebarOpen] = useState(true);

  const user = useUser();
  const { currentOrg } = useCurrentOrg();

  // Use service layer for pull actions
  const pullActions = usePullActions(owner, repo, Number(number));

  // Use controlled queries that prevent auto-updates
  const { pull, pullDiffs, hasUpdates, refresh, refreshKey, isRefreshing } = usePullDataWithRefresh(
    owner,
    repo,
    Number(number)
  );

  const [sortByRelevancy, setSortByRelevancy] = useState(true);

  useEffect(() => {
    if (!pull || !pullDiffs) return;
    // Default to A-Z sorting if walkthrough data isn't available
    if (!pullDiffs.groupedDiffs || pullDiffs.commitSha !== pull.head.sha) {
      if (pullDiffs.isGenerating) {
        console.log(
          'setting sortByRelevancy to false: Head Mismatch with Generation',
          pullDiffs.commitSha,
          pull.head.sha,
          pullDiffs.isGenerating
        );
        setSortByRelevancy(false);
      } else {
        console.log(
          'setting sortByRelevancy to false: Head Mismatch without Generation',
          pullDiffs.commitSha,
          pull.head.sha,
          pullDiffs.isGenerating
        );
        setSortByRelevancy(false);
      }
    } else if (pullDiffs.isGenerating) {
      console.log(
        'setting sortByRelevancy to false: Generating',
        pullDiffs.commitSha,
        pull.head.sha,
        pullDiffs.isGenerating
      );
      setSortByRelevancy(false);
    }
  }, [pull, pullDiffs]);

  const onSortChange = (sortByRelevancy: boolean) => {
    if (sortByRelevancy && pullDiffs && (!pullDiffs.groupedDiffs || pullDiffs.isGenerating)) {
      toast.error('Walkthrough is not available (yet)');
      return;
    }
    setSortByRelevancy(sortByRelevancy);
  };

  const walkthrough: WalkthroughGroup[] = useMemo(() => pullDiffs?.groupedDiffs ?? [], [pullDiffs]);

  const openMergeDialog = (strategy: MergeStrategy) => {
    setDefaultMergeStrategy(strategy);
    setIsMergeDialogOpen(true);
  };

  const handleHeaderTitleVisibility: IntersectionObserverCallback = ([entry]) => {
    setIsHeaderTitleVisible(!entry.isIntersecting);
  };

  // NOTE: intentional - hack to make sure we force re-render and show the header title appropriately.
  useEffect(() => {}, [isHeaderTitleVisible]);

  useEffect(() => {
    const titleObserver = new IntersectionObserver(handleHeaderTitleVisibility, {
      // Start showing header title when main title is 20% visible or less
      threshold: 0,
      rootMargin: '-80px 0px 0px 0px', // Account for header height
    });

    const titleElement = document.querySelector('[data-main-title]');
    if (titleElement) {
      titleObserver.observe(titleElement);
    }

    return () => titleObserver.disconnect();
  }, [handleHeaderTitleVisibility]);

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  return (
    <AiChatProvider owner={owner} repo={repo} pullNumber={number}>
      <div className="flex flex-col size-full overflow-hidden">
        {/* Header */}
        <div className="h-14 shrink-0 bg-stone-100/50 grid grid-cols-[1fr_auto_1fr] items-center px-4 overflow-hidden gap-4 z-0">
          <div className="flex items-center gap-4 h-full">
            <div
              className={cn('p-2 rounded-sm cursor-pointer relative', 'hover:bg-stone-200 select-none')}
              onMouseDown={() => setIsLeftSidebarOpen(!isLeftSidebarOpen)}
            >
              <SidebarSimpleIcon size={22} weight={isLeftSidebarOpen ? 'fill' : 'duotone'} />
            </div>
            {/* <Separator orientation="vertical" className="h-full" /> */}

            <div className="flex items-center gap-4">
              <ReviewersAndReviewTrigger
                onTriggerReview={(params) => {
                  if (!currentOrg) {
                    toast.error('Please select an organization first');
                    return;
                  }

                  pullActions.generateReview.mutate({
                    installationId: currentOrg.id,
                    ...params,
                  });
                }}
                isReviewLoading={pullActions.generateReview.isLoading}
                canTriggerReview={!!pull && !!currentOrg}
              />
              {/* Refresh Button */}
              <AnimatePresence>
                {hasUpdates && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Button
                      size="sm"
                      disabled={isRefreshing}
                      onClick={() =>
                        refresh().catch(() => {
                          toast.error('Failed to refresh data. Please try again.');
                        })
                      }
                      className="bg-orange-100 hover:bg-orange-200 text-orange-700 hover:underline shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <RefreshCwIcon size={14} className={cn('mr-1.5', isRefreshing && 'animate-spin')} />
                      Refresh
                    </Button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
          <div className="w-fit flex items-center">
            <AnimatePresence>
              {isHeaderTitleVisible && (
                <motion.div
                  key="title"
                  className="w-full"
                  initial={{ y: 10, opacity: 0 }}
                  animate={{ y: 0, opacity: 1, transition: { duration: 0.2 } }}
                  exit={{ y: 10, opacity: 0, transition: { duration: 0.1 } }}
                >
                  <div className="flex items-center gap-2 overflow-hidden">
                    <div className="shrink-0">
                      {pull && <StateCell state={pull.state} />}
                      {!pull && <Skeleton className="size-5" />}
                    </div>
                    <h2 className="text-base font-medium truncate leading-none">{pull?.title || 'Loading...'}</h2>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className="flex items-center gap-4 justify-end h-full overflow-hidden">
            {/* Updated and checks */}
            <div className="flex items-center gap-5">
              <PullActivityPopover />
              {pull?.head.sha ? (
                <PullChecksPopover owner={owner} repo={repo} sha={pull.head.sha} />
              ) : (
                <Skeleton className="w-28 rounded-md h-6" />
              )}
              <PullActionButton openMergeDialog={openMergeDialog} />
            </div>
            {/* Comments and AI Chat buttons */}
            <div className="flex items-center justify-end gap-2 h-full">
              {/* <Separator orientation="vertical" className="h-full" /> */}
              <div className="flex items-center gap-1">
                <div
                  className={cn(
                    'px-3 py-2 rounded-sm cursor-pointer relative flex items-center gap-1.5',
                    'hover:bg-stone-200 select-none transition-colors',
                    isRightSidebarOpen && activeTab === 'comments' ? 'bg-stone-200' : ''
                  )}
                  onMouseDown={() => {
                    if (isRightSidebarOpen && activeTab === 'comments') {
                      dispatch({ type: 'SET_RIGHT_SIDEBAR_OPEN', payload: false });
                    } else {
                      dispatch({ type: 'SET_RIGHT_SIDEBAR_OPEN', payload: true });
                      dispatch({ type: 'SET_ACTIVE_TAB', payload: 'comments' });
                    }
                  }}
                >
                  <MessagesSquareIcon size={20} className="text-stone-600" />
                </div>
                <div
                  className={cn(
                    'px-3 py-2 rounded-sm cursor-pointer relative flex items-center gap-1.5',
                    'hover:bg-stone-200 select-none transition-colors',
                    isRightSidebarOpen && activeTab === 'ai-chat' ? 'bg-stone-200' : ''
                  )}
                  onMouseDown={() => {
                    if (isRightSidebarOpen && activeTab === 'ai-chat') {
                      dispatch({ type: 'SET_RIGHT_SIDEBAR_OPEN', payload: false });
                    } else {
                      dispatch({ type: 'SET_RIGHT_SIDEBAR_OPEN', payload: true });
                      dispatch({ type: 'SET_ACTIVE_TAB', payload: 'ai-chat' });
                    }
                  }}
                >
                  <BotMessageSquareIcon size={20} className="text-stone-600" />
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Body */}
        <div className="flex-1 size-full overflow-hidden relative bg-stone-100/50">
          {/* Use CSS Grid with fixed 3-column layout that prevents main content reflow */}
          <div
            className={cn(
              'size-full grid pt-1',
              /* Use CSS grid with proper column sizing to avoid main content reflow */
              {
                'grid-cols-[320px_1fr_420px]': isRightSidebarOpen && isLeftSidebarOpen,
                'grid-cols-[0px_1fr_420px]': isRightSidebarOpen && !isLeftSidebarOpen,
                'grid-cols-[320px_1fr_0px]': !isRightSidebarOpen && isLeftSidebarOpen,
                'grid-cols-[0px_1fr_0px]': !isRightSidebarOpen && !isLeftSidebarOpen,
              }
            )}
          >
            {/* Outline / File tree */}
            <div
              className={cn(
                'h-full flex flex-col overflow-y-auto',
                // 'h-full bg-stone-50 flex flex-col py-4 overflow-y-auto shadow-[inset_-2px_0px_2px_rgba(0,0,0,0.05)]',
                'transition-opacity duration-150 ease-in-out',
                isLeftSidebarOpen ? 'opacity-100' : 'opacity-0'
              )}
            >
              <FilePathsTree
                sortByRelevancy={sortByRelevancy}
                walkthrough={walkthrough}
                isGenerating={pullDiffs?.isGenerating ?? true}
                onSortChange={onSortChange}
              />
            </div>

            {/* Main content - fixed width with isolated scroll container */}
            <div
              key={refreshKey}
              className={cn(
                `flex flex-col bg-stone-50 items-center overflow-y-auto contain-strict size-full z-40 rounded-md shadow-[0_0_6px_-2px_rgba(0,0,0,0.2)]`,
                isLeftSidebarOpen ? '' : 'rounded-l-none',
                isRightSidebarOpen ? '' : 'rounded-r-none'
              )}
              ref={scrollContainerRef}
            >
              <PullDescription />

              <PullDiscussion />

              {pull && (
                <PullWalkthrough
                  pullNodeId={pull.nodeId}
                  pullHeadSha={pull.head.sha}
                  walkthrough={walkthrough}
                  isGenerating={pullDiffs?.isGenerating ?? true}
                  sortByRelevancy={sortByRelevancy}
                  diff={pullDiffs?.diff || ''}
                />
              )}

              <MergeDialog
                isOpen={isMergeDialogOpen}
                onOpenChange={setIsMergeDialogOpen}
                defaultMergeStrategy={defaultMergeStrategy}
              />
            </div>

            {/* Comments sidebar with fixed-width column that animates opacity only */}
            <div
              className={cn(
                'h-full flex flex-col overflow-hidden',
                'transition-opacity duration-150 ease-in-out',
                isRightSidebarOpen ? 'opacity-100' : 'opacity-0'
              )}
            >
              <CommentsSidebar />
            </div>
          </div>
          {(import.meta.env.DEV ||
            user.user?.emailAddresses.some((email) => email.emailAddress.includes('@withmesa.com'))) && (
            <div className="absolute bottom-5 left-2 flex flex-col gap-2">
              <button
                type="button"
                title="Generate walkthrough"
                className="text-stone-600 bg-stone-200 px-2 py-2 rounded-full text-xs hover:cursor-pointer flex items-center justify-center"
                disabled={pullActions.generateWalkthrough.isLoading}
                onClick={() => pullActions.generateWalkthrough.mutate()}
              >
                <SparklesIcon size={16} className="mr-1" />
                <span>{pullActions.generateWalkthrough.isLoading ? 'Generating...' : 'Walkthrough'}</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </AiChatProvider>
  );
}
